import {
  Avatar,
  Button,
  Grid2,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { IconButton, Icons } from 'utoniq-ui'
import { SkeletonText } from 'utoniq-ui'

import {
  CollectionDetailConnectionFragment,
  CollectionDetailQuery,
  CollectionItemsConnectionFragment,
  CollectionLabel,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, Header, HeaderImageArea } from '@/components/layouts'
import { CollectionIndicator, ItemImage } from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  user: Maybe<CollectionDetailQuery['user']>
  userId?: string
  collection: Maybe<
    {
      collectionItems: CollectionItemsConnectionFragment
    } & CollectionDetailConnectionFragment
  >
  collectionItems: Maybe<CollectionItemsConnectionFragment>
}

type BannerCellProps = {
  thumbnail: Maybe<CollectionDetailConnectionFragment['thumbnail']>
  title: Maybe<CollectionDetailConnectionFragment['title']>
  artistId?: string
}

type CollectionOverviewTextCellProps = {
  title: Maybe<CollectionDetailConnectionFragment['title']>
  description: Maybe<CollectionDetailConnectionFragment['description']>
  artist: Maybe<CollectionDetailConnectionFragment['artist']>
}

type CollectionRateCellProps = {
  user: State['user']
  itemCount: Maybe<CollectionDetailConnectionFragment['itemCount']>
  userCollection: Maybe<CollectionDetailConnectionFragment['userCollection']>
}

type SupplementLinkSetCellProps = {
  loading?: State['loading']
  onClickShare: () => Promise<void>
}

type ItemImageListCellProps = {
  loading: boolean
  hasNextPage: boolean
  userId?: string
  artistId: string
  collectionId: string
  collectionItems: Maybe<CollectionItemsConnectionFragment>
  userCollection: Maybe<CollectionDetailConnectionFragment['userCollection']>
  onFetchMore: () => Promise<void>
}

type CollectionDetailLoadingProps = {}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onChangeLabel: (isFirstLabel: boolean, name: string) => Promise<void>
  onClickShare: () => Promise<void>
  onFetchMore: (isFirstLabel: boolean, label?: string) => Promise<void>
}

const defaultLabels: Maybe<Pick<CollectionLabel, 'id' | 'name'>>[] = [null]

/**
 * アーティストのコレクション詳細
 * https://zpl.io/2EQ56Ky
 */
export const CollectionDetailTemplate: Template<State, Action> = ({
  state: {
    collection,
    collectionItems,
    loading,
    fetching,
    hasNextPage,
    user,
    userId,
  },
  onChangeLabel,
  onClickShare,
  onFetchMore,
}) => {
  const [currentTab, setCurrentTab] = useState<number>(0)
  const { routerNames } = useRouter()

  const labels = useMemo(() => {
    if (collection?.labels?.length) {
      return collection.labels
    }
    return defaultLabels
  }, [collection?.labels])

  const handleChangeTab = useCallback(
    async (number: number) => {
      if (!collection?.labels?.length) {
        return
      }
      setCurrentTab(number)
      await onChangeLabel(number === 0, collection.labels[number]?.name ?? '')
    },
    [onChangeLabel, collection?.labels]
  )

  return (
    <ArtistThemeProvider artistId={collection?.artistId}>
      <BaseLayout safeArea={{ pt: 0 }}>
        <Head
          title={[
            routerNames.CollectionDetail,
            collection?.title,
            user?.displayName,
          ]}
        />
        {loading ? (
          <>
            <CollectionDetailLoading />
            <SupplementLinkSetCell
              loading={loading}
              onClickShare={onClickShare}
            />
          </>
        ) : (
          <>
            <BannerCell
              title={collection?.title ?? ''}
              thumbnail={collection?.thumbnail ?? null}
              artistId={collection?.artistId}
            />
            <CollectionOverviewTextCell
              title={collection?.title ?? null}
              description={collection?.description ?? null}
              artist={collection?.artist ?? null}
            />
            {/* ログイン時には、ユーザーの達成状況をプログレスバーで表示 */}
            {user && (
              <CollectionRateCell
                user={user}
                userCollection={collection?.userCollection ?? null}
                itemCount={collection?.itemCount ?? null}
              />
            )}
            <SupplementLinkSetCell onClickShare={onClickShare} />
          </>
        )}
        <Tabs
          labels={collection?.labels?.map(label => label?.name ?? '') ?? []}
          hiddenLabel={!collection?.labels?.length || loading}
          value={currentTab}
          disableAnimateTransitions
          onChange={handleChangeTab}
        >
          {labels.map((label, index) => (
            <ItemImageListCell
              key={index}
              loading={loading || fetching}
              userId={userId}
              hasNextPage={hasNextPage}
              collectionId={collection?.id ?? ''}
              artistId={collection?.artistId ?? ''}
              collectionItems={collectionItems ?? null}
              userCollection={collection?.userCollection ?? null}
              onFetchMore={() => onFetchMore(index === 0, label?.name)}
            />
          ))}
        </Tabs>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

export const BannerCell: Cell<BannerCellProps> = ({
  thumbnail,
  title,
  artistId,
}) => {
  const headerRef = useRef() as RefObject<HTMLDivElement>
  const headerImageAreaRef = useRef() as RefObject<HTMLDivElement>
  const { router, generatePath, routerPages } = useRouter()
  const thumbnailUri = thumbnail?.mediumUri ?? thumbnail?.uri ?? ''

  useEffect(() => {
    function handleScroll(this: Window) {
      if (headerRef.current && headerImageAreaRef.current) {
        const bannerImageHeight = headerImageAreaRef.current?.clientHeight ?? 1
        headerRef.current.style.opacity = `${
          (this.scrollY - 20) / bannerImageHeight
        }`
        headerRef.current.style.display = `${
          this.scrollY > 20 ? 'unset' : 'none'
        }`
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleClose = () =>
    router?.goBack(
      artistId &&
        generatePath(
          routerPages[
            // アーティストタブにmyCollectionsが含まれている場合ブラウザバックURLを変更
            config.artistTabs.includes('myCollections')
              ? 'ArtistMyCollections'
              : 'ArtistCollections'
          ],
          { artistId }
        )
    )

  return (
    <>
      <Header
        elevation={0}
        position="fixed"
        color="transparent"
        sx={{
          maxWidth: 600,
          bgcolor: 'transparent',
          pt: styles.safeAreaTop,
        }}
        left={
          <IconButton.Close
            edge="start"
            size="medium"
            onClick={handleClose}
            bgcolor="grey.50"
          />
        }
      />
      <Header
        ref={headerRef}
        position="fixed"
        color="primary"
        sx={{
          maxWidth: 600,
          opacity: 0,
          pt: styles.safeAreaTop,
        }}
        left={
          <IconButton.Close
            edge="start"
            size="medium"
            onClick={handleClose}
            color="white"
          />
        }
        title={title}
      />
      <HeaderImageArea
        ref={headerImageAreaRef}
        imageUri={thumbnailUri}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      />
    </>
  )
}

export const CollectionOverviewTextCell: Cell<CollectionOverviewTextCellProps> =
  ({ artist, title, description }) => (
    <CellFrame p={5}>
      <Typography variant="subtitle2" color="text.hint">
        {artist?.displayName}
      </Typography>
      <Typography variant="h3" pt={1}>
        {title}
      </Typography>
      <Typography variant="body2" pt={3}>
        {description}
      </Typography>
    </CellFrame>
  )

export const CollectionRateCell: Cell<CollectionRateCellProps> = ({
  userCollection,
  user,
  itemCount,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('collectionDetail')
  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack direction="row" spacing={3} sx={{ pt: 2 }} alignItems="center">
        <Stack
          component={Link}
          href={generatePath(routerPages.UserProfile, { userId: user?.id })}
          direction="row"
          spacing={3}
          alignItems="center"
        >
          <Avatar
            src={user?.avatar?.smallUri ?? ''}
            sx={{ width: 28, height: 28 }}
          />
          <Typography variant="subtitle2" color="text.secondary">
            {user?.displayName}
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('numberOfOwned', {
              number: userCollection?.totalOwnedCount ?? 0,
            })}
          </Typography>
        </Stack>
      </Stack>
      <CollectionIndicator
        sx={{ py: 2, mx: 0 }}
        itemCount={userCollection?.totalOwnedItemCount ?? 0}
        totalCount={itemCount ?? 0}
      />
    </CellFrame>
  )
}

export const SupplementLinkSetCell: Cell<SupplementLinkSetCellProps> = ({
  loading,
  onClickShare,
}) => {
  const { t } = useTranslation('collectionDetail')
  const supplements = useMemo(
    () => [
      {
        id: 1,
        label: t('share'),
        buttonProps: { onClick: onClickShare },
        icon: <Icons.Share sx={{ mx: 'auto' }} />,
      },
    ],
    [onClickShare, t]
  )
  return (
    <CellFrame>
      {loading ? (
        <Stack
          direction="row"
          sx={{ py: 2, px: 5 }}
          spacing={3}
          justifyContent="end"
        >
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            sx={{ borderRadius: 0.5 }}
          />
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            sx={{ borderRadius: 0.5 }}
          />
        </Stack>
      ) : (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', pt: 2 }}>
          {supplements.map((supplement, index) => (
            <Button
              key={index}
              {...supplement.buttonProps}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                p: 1,
                color: 'text.primary',
              }}
              variant="text"
            >
              {supplement.icon}
              <Typography variant="overline" color="hint" sx={{ pt: 1 }}>
                {supplement.label}
              </Typography>
            </Button>
          ))}
        </Stack>
      )}
    </CellFrame>
  )
}

export const ItemImageListCell: Cell<ItemImageListCellProps> = ({
  loading,
  hasNextPage,
  userId,
  artistId,
  collectionId,
  collectionItems,
  userCollection,
  onFetchMore,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  const getData = useCallback(
    (node: CollectionItemsConnectionFragment['edges'][number]['node']) => {
      const ownedItem = userCollection?.ownedItems?.find(
        o => o.itemId === node.id
      )
      const ownedCount =
        node.item.sku && ownedItem?.userItem
          ? ownedItem?.userItem.serials.filter(
              serial => serial.serial?.skuId === node.item.sku?.id
            ).length
          : ownedItem?.ownedCount
      const isNoItem = !ownedItem || !ownedCount
      let href
      if (isNoItem) {
        href = `${generatePath(routerPages.CollectionItemDetail, {
          itemId: node.item.id,
          collectionId: collectionId,
        })}${node.item.sku ? `?skuId=${encodeSkuId(node.item.sku?.id)}` : ''}`
      } else {
        href = userId
          ? `${generatePath(routerPages.UserItemDetail, {
              itemId: node.item.id,
              userId: userId,
            })}${
              node.item.sku ? `?skuId=${encodeSkuId(node.item.sku?.id)}` : ''
            }`
          : `${generatePath(routerPages.OwnedItemDetail, {
              itemId: node.item.id,
            })}${
              node.item.sku
                ? `?skuId=${encodeSkuId(node.item.sku?.id ?? '')}`
                : ''
            }`
      }
      return { ownedCount: ownedCount, isNoItem, href }
    },
    [
      collectionId,
      generatePath,
      routerPages.CollectionItemDetail,
      routerPages.OwnedItemDetail,
      routerPages.UserItemDetail,
      userCollection?.ownedItems,
      userId,
    ]
  )

  return (
    <CellFrame sx={{ px: 5, pt: 3 }}>
      <ScrollFetcher
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid2}
        itemComponentProps={{ size: { xs: 4, md: 3 } }}
        nodes={collectionItems?.edges?.map(({ node }, index) => {
          const { ownedCount, isNoItem, href } = getData(node)
          return (
            <ItemImage
              href={href}
              onClick={() => {
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  artistId,
                  collectionId: collectionId,
                  itemId: node?.item.id,
                  name: 'collectionDetail',
                })
              }}
              showShadow
              key={index}
              item={node.item}
              noItem={isNoItem}
              ownedCount={ownedCount || 0}
            />
          )
        })}
        loadingComponent={
          <Grid2 container spacing={3} sx={{ py: 2 }}>
            {Array.from(Array(12).keys()).map(key => (
              <Grid2 size={{ xs: 4, md: 3 }} key={key}>
                <Skeleton variant="rectangular" width="100%" height={166} />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}

export const CollectionDetailLoading: Cell<CollectionDetailLoadingProps> =
  () => {
    const { router } = useRouter()
    return (
      <CellFrame>
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{
            height: 'calc(100vw *  212 / 375)',
            maxHeight: 'calc(600px * 212 / 375)',
            '& header': { visibility: 'visible' },
          }}
        >
          <Header
            color="transparent"
            left={
              <IconButton.Close
                edge="start"
                size="medium"
                onClick={() => router?.goBack()}
                bgcolor="grey.50"
              />
            }
          />
        </Skeleton>
        <Stack sx={{ pt: 5, px: 5, pb: 3 }} spacing={2}>
          <Skeleton
            variant="rectangular"
            sx={{ width: 84, height: 10, borderRadius: 0.5 }}
          />
          <Skeleton
            variant="rectangular"
            sx={{ width: 252, height: 20, borderRadius: 0.5 }}
          />
          <Skeleton
            variant="rectangular"
            sx={{ width: 300, height: 15, borderRadius: 0.5 }}
          />
          <Skeleton
            variant="rectangular"
            sx={{ width: 252, height: 15, borderRadius: 0.5 }}
          />
        </Stack>
        <Stack spacing={2} sx={{ py: 2, px: 5 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={28} height={28} />
            <SkeletonText width="25%" variant="small" />
            <SkeletonText width="25%" variant="small" />
          </Stack>
          <Stack direction="row" spacing={4} alignItems="center">
            <SkeletonText variant="small" width="100%" />
            <Skeleton width={30} sx={{ borderRadius: 0.5, minHeight: 11 }} />
          </Stack>
        </Stack>
      </CellFrame>
    )
  }
